import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Form, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import type { Personnel } from '@/types/personnel';
import { useAuth } from '@/contexts/AuthContext';
import { useAvatar } from '@/contexts/AvatarContext';
import { usersService } from '@/services/users.service';
import { buildFullUrl, fetchImageAsDataUri } from '@/contexts/AvatarContext';
import { loadAvatarFromData } from '@/utils/avatarLoader';
import { formatDateDisplay } from '@/utils/dateHelpers';
import { parseCurrencyInput } from '@/utils/formatters';
import { handleApiError } from '@/utils/errorHandler';
import { buildUserUpdatePayload } from '@shared/utils/buildUserUpdatePayload';
import { getUserFriendlyErrorMessage } from '@shared/services';

export interface PersonalInfoFormData {
  profilePicture?: string;
  fullName: string;
  description?: string;
  placeOfBirth?: string;
  hometown?: string;
  nationality?: string;
  ethnicity?: string;
  dateOfBirth?: string;
  gender?: string;
  religion?: string;
  maritalStatus?: string;
  militaryService?: string;
  personalTaxCode?: string;
  startDate?: string;
  contractSigningDate?: string;
  educationLevel?: string;
  yearsOfExperience?: string;
  major?: string;
  highestEducationLevel?: string;
  directManager?: string;
  laborType?: string;
  // Thông tin ngân hàng
  bankAccountNumber?: string;
  bankAccountName?: string;
  bankName?: string;
  bankBranch?: string;
}

export interface UseUpdatePersonalInfoResult {
  // form
  form: ReturnType<typeof Form.useForm>[0];
  loading: boolean;
  initialDataStatus: 'loading' | 'ready' | 'error';
  uploading: boolean;
  profileImage: string | null;
  setProfileImage: (image: string | null) => void;
  
  // UI state
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  selectedMenuKey: string;
  setSelectedMenuKey: (key: string) => void;
  isHovered: boolean;
  setIsHovered: (hovered: boolean) => void;
  
  // computed
  isViewingOwnProfile: boolean;
  canEditAvatar: boolean;
  
  // actions
  handleSubmit: () => Promise<'submitted' | 'no-changes' | 'failed'>;
  handleValuesChange: (changedValues: Record<string, unknown>) => void;
  handleCancel: () => void;
  handleImageUpload: (file: File) => Promise<boolean>;
  handleMenuClick: (e: { key: string }) => void;
  parseDate: (dateString?: string) => Dayjs | undefined;
  convertDbValueToFormValue: (fieldName: string, value: any) => any;
}

// Helper function để convert giá trị từ database (tiếng Anh) sang giá trị form (tiếng Việt)
const convertDbValueToFormValueHelper = (fieldName: string, value: any): any => {
  if (!value || value === '') return value;
  
  const strValue = String(value).toLowerCase().trim();
  
  // Convert gender
  if (fieldName === 'gender') {
    const genderMap: Record<string, string> = {
      'male': 'Nam',
      'female': 'Nữ',
      'other': 'Khác',
    };
    return genderMap[strValue] || value;
  }
  
  // Convert marital status
  if (fieldName === 'maritalStatus') {
    const maritalStatusMap: Record<string, string> = {
      'single': 'Độc thân',
      'married': 'Đã kết hôn',
      'divorced': 'Ly hôn',
      'widowed': 'Góa',
    };
    return maritalStatusMap[strValue] || value;
  }
  
  // Convert military service
  if (fieldName === 'militaryService') {
    const militaryServiceMap: Record<string, string> = {
      'yes': 'Đã hoàn thành',
      'no': 'Hoãn nghĩa vụ',
      'exempt': 'Miễn nghĩa vụ',
    };
    return militaryServiceMap[strValue] || value;
  }
  
  // Convert labor type
  if (fieldName === 'laborType') {
    const laborTypeMap: Record<string, string> = {
      'full-time': 'Toàn thời gian',
      'fulltime': 'Toàn thời gian',
      'part-time': 'Bán thời gian',
      'parttime': 'Bán thời gian',
      'contract': 'Hợp đồng',
      'intern': 'Thực tập',
      'internship': 'Thực tập',
    };
    return laborTypeMap[strValue] || value;
  }
  
  // Convert general education level
  if (fieldName === 'educationLevel' || fieldName === 'generalEducationLevel' || fieldName === 'highestEducationLevel') {
    const generalEducationLevelMap: Record<string, string> = {
      'primary': 'Tiểu học',
      'secondary': 'Trung học cơ sở',
      'highschool': 'Trung học phổ thông',
    };
    return generalEducationLevelMap[strValue] || value;
  }
  
  // Convert academic education level
  if (fieldName === 'academicEducationLevel') {
    const academicEducationLevelMap: Record<string, string> = {
      'secondary': 'Trung cấp',
      'college': 'Cao đẳng',
      'university': 'Đại học',
      'graduate': 'Sau đại học',
    };
    return academicEducationLevelMap[strValue] || value;
  }
  
  // Convert ethnicity
  if (fieldName === 'ethnicity') {
    const ethnicityMap: Record<string, string> = {
      'kinh': 'Kinh',
      'tay': 'Tày',
      'thai': 'Thái',
      'muong': 'Mường',
      'khmer': 'Khmer',
      'hoa': 'Hoa',
      'nung': 'Nùng',
      'hmong': 'H\'Mông',
      'dao': 'Dao',
      'giarai': 'Gia Rai',
      'ede': 'Ê Đê',
      'bana': 'Ba Na',
      'xodang': 'Xơ Đăng',
      'sanchay': 'Sán Chay',
      'coho': 'Cơ Ho',
      'cham': 'Chăm',
      'sandiu': 'Sán Dìu',
      'hre': 'Hrê',
      'mnong': 'M\'Nông',
      'raglai': 'Ra Glai',
      'stieng': 'Stiêng',
      'bruvankieu': 'Bru - Vân Kiều',
      'tho': 'Thổ',
      'giay': 'Giáy',
      'co': 'Cơ Tu',
      'taoi': 'Tà Ôi',
      'ma': 'Mạ',
      'khmu': 'Khơ Mú',
      'boy': 'Bố Y',
      'xinhmun': 'Xinh Mun',
      'cong': 'Cống',
      'hanhi': 'Hà Nhì',
      'lahu': 'La Hủ',
      'phula': 'Phù Lá',
      'lachi': 'La Chí',
      'pathen': 'Pa Then',
      'churu': 'Chu Ru',
      'lao': 'Lào',
      'laha': 'La Ha',
      'khang': 'Kháng',
      'lu': 'Lự',
      'lolo': 'Lô Lô',
      'mang': 'Mảng',
      'phathay': 'Pà Thẻn',
      'colao': 'Cờ Lao',
      'brau': 'Brâu',
      'odu': 'Ơ Đu',
      'romam': 'Rơ Măm',
      'sila': 'Si La',
      'pupeo': 'Pu Péo',
      'other': 'Khác',
    };
    return ethnicityMap[strValue] || value;
  }
  
  return value;
};

export const useUpdatePersonalInfo = (
  propInitialData?: PersonalInfoFormData,
  onSubmit?: (data: PersonalInfoFormData) => Promise<void>
): UseUpdatePersonalInfoResult => {
  const { user } = useAuth();
  const { avatarUri, setAvatarUri } = useAvatar();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedMenuKey, setSelectedMenuKey] = useState<string>('personal-info');
  const [isHovered, setIsHovered] = useState(false);
  const hasFetchedData = useRef<number | null>(null);
  const [originalData, setOriginalData] = useState<any>(null);
  const [initialDataStatus, setInitialDataStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // setFieldsValue khi tải hồ sơ không gọi onValuesChange; chỉ ghi nhận sửa đổi từ người dùng.
  const editedCollections = useRef(new Set<string>());

  const handleValuesChange = useCallback((changedValues: Record<string, unknown>) => {
    for (const key of ['familyDependents', 'educationHistories', 'workExperiences', 'certificates', 'skills']) {
      if (Object.prototype.hasOwnProperty.call(changedValues, key)) {
        editedCollections.current.add(key);
      }
    }
  }, []);

  const personnelData = (location.state as { personnel?: Personnel })?.personnel;
  
  // Xác định xem đang xem hồ sơ của chính mình hay của người khác
  const isViewingOwnProfile =
    !personnelData || String(personnelData.id) === String(user?.id ?? '');
  
  // Kiểm tra xem user hiện tại có quyền chỉnh sửa avatar không
  // Cho phép nếu: đang xem hồ sơ của chính mình HOẶC (HR/CEO/admin đang chỉnh sửa hồ sơ của staff)
  const canEditAvatar = useMemo(() => {
    if (isViewingOwnProfile) return true;
    
    // Kiểm tra role của user hiện tại
    const currentUserRole = user?.role?.toLowerCase();
    const isAdminOrHROrCEO = currentUserRole === 'admin' || currentUserRole === 'hr' || currentUserRole === 'ceo';
    
    if (!isAdminOrHROrCEO) return false;
    
    // Kiểm tra role của user đang được chỉnh sửa (phải là staff)
    const targetUserRole = personnelData?.role?.toLowerCase();
    return targetUserRole === 'staff';
  }, [isViewingOwnProfile, user?.role, personnelData?.role]);

  // Parse date helper
  const parseDate = useCallback((dateString?: string): Dayjs | undefined => {
    if (!dateString) return undefined;
    const parsed = dayjs(dateString, ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'], true);
    return parsed.isValid() ? parsed : undefined;
  }, []);

  // Convert DB value to form value
  const convertDbValueToFormValue = useCallback((fieldName: string, value: any): any => {
    return convertDbValueToFormValueHelper(fieldName, value);
  }, []);

  // Tải toàn bộ dữ liệu gốc trước khi cho gửi yêu cầu cập nhật.
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const userId = personnelData?.id 
          ? parseInt(personnelData.id, 10)
          : user?.id ? parseInt(user.id.toString(), 10) : NaN;
        
        if (isNaN(userId)) return;
        if (hasFetchedData.current === userId) return;
        
        hasFetchedData.current = userId;
        editedCollections.current.clear();
        setInitialDataStatus('loading');
        const [userDetail, familyMembers, certificates, educationHistory, workExperience] = await Promise.all([
          isViewingOwnProfile
            ? usersService.getProfile()
            : usersService.getUserById(userId),
          usersService.getFamilyMembers(userId),
          usersService.getCertificates(userId),
          usersService.getEducationHistory(userId),
          usersService.getWorkExperience(userId),
        ]);
        const normalizedFamilyDependents = (familyMembers || []).map((member) => ({
          relationship: member.relationship || '',
          fullName: member.fullName || '',
          birthYear:
            member.birthYear !== undefined && member.birthYear !== null
              ? String(member.birthYear)
              : '',
          occupation: member.occupation || '',
          workplace: member.workplace || '',
          phone: member.phone || '',
          address: member.address || '',
        }));

        const normalizedCertificates = (certificates || []).map((cert) => ({
          id: cert.id,
          certificateNumber: cert.certificateNumber || '',
          issuingUnit: cert.issuingUnit || '',
          certificateType: cert.certificateType || '',
          fromDate: cert.fromDate ? dayjs(cert.fromDate).format('YYYY-MM-DD') : undefined,
          toDate: cert.toDate ? dayjs(cert.toDate).format('YYYY-MM-DD') : undefined,
          notes: cert.notes || '',
        }));

        const normalizedEducationHistories = (educationHistory || []).map((edu) => ({
          fromDate: edu.fromDate ? dayjs(edu.fromDate).format('YYYY-MM-DD') : undefined,
          toDate: edu.toDate ? dayjs(edu.toDate).format('YYYY-MM-DD') : undefined,
          trainingType: edu.trainingForm || '',
          major: edu.major || '',
          educationLevel: edu.educationLevel || '',
          school: edu.trainingInstitution || '',
        }));

        const normalizedWorkExperiences = (workExperience || []).map((experience) => ({
          fromDate: experience.fromDate ? dayjs(experience.fromDate).format('YYYY-MM-DD') : undefined,
          toDate: experience.toDate ? dayjs(experience.toDate).format('YYYY-MM-DD') : undefined,
          company: experience.company || '',
          department: experience.department || '',
          title: experience.position || '',
          position: experience.jobLocation || '',
          reference: experience.workReferencePerson || '',
          salary: experience.salary === undefined || experience.salary === null ? undefined : Number(experience.salary),
        }));

        let normalizedSkills: string[] = [];
        if ((userDetail as any)?.skills) {
          const skillsData = (userDetail as any).skills;
          if (Array.isArray(skillsData)) {
            normalizedSkills = skillsData.map((s: any) => String(s).trim()).filter(Boolean);
          } else if (typeof skillsData === 'string') {
            try {
              const parsed = JSON.parse(skillsData);
              if (Array.isArray(parsed)) {
                normalizedSkills = parsed.map((s: any) => String(s).trim()).filter(Boolean);
              } else {
                normalizedSkills = skillsData.split(',').map((s) => s.trim()).filter(Boolean);
              }
            } catch {
              normalizedSkills = skillsData.split(',').map((s) => s.trim()).filter(Boolean);
            }
          }
        }

        const original: any = {
          fullName: userDetail.fullName,
          selfDescription: userDetail.selfDescription,
          placeOfBirth: userDetail.placeOfBirth || '',
          hometown: userDetail.hometown || '',
          nationality: userDetail.nationality || '',
          ethnicity: userDetail.ethnicity,
          dateOfBirth: userDetail.dateOfBirth ? dayjs(userDetail.dateOfBirth).format('YYYY-MM-DD') : undefined,
          gender: userDetail.gender,
          religion: userDetail.religion || '',
          maritalStatus: userDetail.maritalStatus,
          militaryService: userDetail.militaryService,
          personalTaxCode: userDetail.personalTaxCode || '',
          // Thông tin liên hệ
          phone: userDetail.phone || '',
          personalEmail: userDetail.personalEmail || '',
          permanentAddress: userDetail.permanentAddress || '',
          permanentWard: userDetail.permanentWard || '',
          currentAddress: userDetail.currentAddress || '',
          currentWard: userDetail.currentWard || '',
          // ID documents
          idNumber: userDetail.idNumber || '',
          idIssuePlace: userDetail.idIssuePlace || '',
          idIssueDate: userDetail.idIssueDate ? dayjs(userDetail.idIssueDate).format('YYYY-MM-DD') : undefined,
          idExpiryDate: userDetail.idExpiryDate ? dayjs(userDetail.idExpiryDate).format('YYYY-MM-DD') : undefined,
          passportType: userDetail.passportType || '',
          passportNumber: userDetail.passportNumber || '',
          passportIssuePlace: userDetail.passportIssuePlace || '',
          passportIssueDate: userDetail.passportIssueDate ? dayjs(userDetail.passportIssueDate).format('YYYY-MM-DD') : undefined,
          passportExpiryDate: userDetail.passportExpiryDate ? dayjs(userDetail.passportExpiryDate).format('YYYY-MM-DD') : undefined,
          startDate: userDetail.startDate ? dayjs(userDetail.startDate).format('YYYY-MM-DD') : undefined,
          officialContractDate: userDetail.officialContractDate ? dayjs(userDetail.officialContractDate).format('YYYY-MM-DD') : undefined,
          generalEducationLevel: userDetail.generalEducationLevel,
          yearsOfExperience: userDetail.yearsOfExperience,
          specialization: userDetail.specialization || '',
          directManager: userDetail.directManager || '',
          laborType: userDetail.laborType,
          company: userDetail.company || '',
          departmentId: userDetail.departmentId ?? null,
          position: userDetail.position || '',
          jobLocation: userDetail.jobLocation || '',
          referencePerson: userDetail.referencePerson || '',
          salary: userDetail.salary !== null && userDetail.salary !== undefined ? Number(userDetail.salary) : undefined,
          bankAccountNumber: userDetail.bankAccountNumber || '',
          bankAccountName: userDetail.bankAccountName || '',
          bankName: userDetail.bankName || '',
          bankBranch: userDetail.bankBranch || '',
          familyDependents: normalizedFamilyDependents,
          educationHistories: normalizedEducationHistories,
          workExperiences: normalizedWorkExperiences,
          certificates: normalizedCertificates,
          skills: normalizedSkills,
        };
        setOriginalData(original);
        
        // Map user detail sang form data và set vào form
        form.setFieldsValue({
          fullName: userDetail.fullName,
          description: userDetail.selfDescription,
          placeOfBirth: userDetail.placeOfBirth || '',
          hometown: userDetail.hometown || '',
          nationality: userDetail.nationality || '',
          ethnicity: convertDbValueToFormValue('ethnicity', userDetail.ethnicity),
          dateOfBirth: parseDate(userDetail.dateOfBirth),
          gender: convertDbValueToFormValue('gender', userDetail.gender),
          religion: userDetail.religion,
          maritalStatus: convertDbValueToFormValue('maritalStatus', userDetail.maritalStatus),
          militaryService: convertDbValueToFormValue('militaryService', userDetail.militaryService),
          personalTaxCode: userDetail.personalTaxCode,
          // Thông tin liên hệ
          phone: userDetail.phone,
          personalEmail: userDetail.personalEmail,
          permanentAddress: userDetail.permanentAddress,
          permanentWard: userDetail.permanentWard,
          currentAddress: userDetail.currentAddress,
          currentWard: userDetail.currentWard,
          // ID documents
          idNumber: userDetail.idNumber,
          idIssuePlace: userDetail.idIssuePlace,
          idIssueDate: parseDate(userDetail.idIssueDate),
          idExpiryDate: parseDate(userDetail.idExpiryDate),
          passportType: userDetail.passportType,
          passportNumber: userDetail.passportNumber,
          passportIssuePlace: userDetail.passportIssuePlace,
          passportIssueDate: parseDate(userDetail.passportIssueDate),
          passportExpiryDate: parseDate(userDetail.passportExpiryDate),
          startDate: parseDate(userDetail.startDate),
          contractSigningDate: parseDate(userDetail.officialContractDate),
          educationLevel: convertDbValueToFormValue('educationLevel', userDetail.generalEducationLevel),
          yearsOfExperience: userDetail.yearsOfExperience?.toString(),
          major: userDetail.specialization,
          highestEducationLevel: convertDbValueToFormValue('highestEducationLevel', userDetail.generalEducationLevel),
          directManager: userDetail.directManager,
          laborType: convertDbValueToFormValue('laborType', userDetail.laborType),
          currentCompany: userDetail.company,
          currentDepartment: userDetail.departmentId,
          currentTitle: userDetail.position,
          currentPosition: userDetail.jobLocation,
          currentReference: userDetail.referencePerson,
          currentSalary: userDetail.salary !== null && userDetail.salary !== undefined ? String(userDetail.salary) : undefined,
          currentStartDate: parseDate(userDetail.startDate),
          bankAccountNumber: userDetail.bankAccountNumber || '',
          bankAccountName: userDetail.bankAccountName || '',
          bankName: userDetail.bankName || '',
          bankBranch: userDetail.bankBranch || '',
          familyDependents: normalizedFamilyDependents,
          educationHistories: normalizedEducationHistories.map((item) => ({
            ...item,
            fromDate: item.fromDate ? dayjs(item.fromDate) : undefined,
            toDate: item.toDate ? dayjs(item.toDate) : undefined,
          })),
          workExperiences: normalizedWorkExperiences.map((item) => ({
            ...item,
            salary: item.salary === undefined ? '' : String(item.salary),
            fromDate: item.fromDate ? dayjs(item.fromDate) : undefined,
            toDate: item.toDate ? dayjs(item.toDate) : undefined,
          })),
          certificates: normalizedCertificates.map((item, index) => ({
            ...item,
            attachments: certificates[index]?.attachments || [],
            issuingBody: item.issuingUnit,
            fromDate: item.fromDate ? dayjs(item.fromDate, 'YYYY-MM-DD') : undefined,
            toDate: item.toDate ? dayjs(item.toDate, 'YYYY-MM-DD') : undefined,
          })),
          skills: normalizedSkills,
        });
        setInitialDataStatus('ready');
        
        // Set avatar nếu có
        try {
          const avatar = await loadAvatarFromData(userDetail.avatar);
          if (avatar) setProfileImage(avatar);
        } catch (avatarError) {
          console.error('Error loading profile avatar:', avatarError);
        }
      } catch (error) {
        console.error('Error fetching initial user data:', error);
        hasFetchedData.current = null;
        setInitialDataStatus('error');
      }
    };
    
    fetchInitialData();
  }, [personnelData?.id, user?.id, propInitialData, form, parseDate, convertDbValueToFormValue, isViewingOwnProfile]);

  // Load avatar: nếu đang xem hồ sơ của người khác thì dùng avatar từ personnelData, nếu không thì dùng từ context
  // Sử dụng ref để track xem có đang upload không, tránh useEffect ghi đè khi upload
  const isUploadingRef = useRef(false);
  
  useEffect(() => {
    const loadAvatar = async () => {
      // Nếu đang upload, không load lại avatar (tránh ghi đè preview)
      if (isUploadingRef.current) {
        return;
      }

      if (isViewingOwnProfile) {
        // Đang xem/chỉnh sửa hồ sơ của chính mình -> dùng avatar từ context (đã là base64)
        if (avatarUri) {
          setProfileImage(avatarUri);
        }
      } else {
        // Đang xem hồ sơ của người khác -> fetch avatar với auth header
        let avatarUrl: string | null = null;
        
        if (personnelData?.avatar) {
          avatarUrl = buildFullUrl(personnelData.avatar);
        } else if (propInitialData?.profilePicture) {
          avatarUrl = buildFullUrl(propInitialData.profilePicture);
        }

        if (avatarUrl) {
          const avatar = await loadAvatarFromData(avatarUrl);
          setProfileImage(avatar);
        } else {
          setProfileImage(null);
        }
      }
    };

    loadAvatar();
  }, [avatarUri, personnelData, propInitialData, isViewingOwnProfile]);

  // Set form từ propInitialData nếu có
  useEffect(() => {
    if (propInitialData && !hasFetchedData.current) {
      // Lưu giá trị ban đầu từ propInitialData
      const original: any = {
        fullName: propInitialData.fullName || '',
        selfDescription: propInitialData.description || '',
        placeOfBirth: propInitialData.placeOfBirth || '',
        hometown: propInitialData.hometown || '',
        nationality: propInitialData.nationality || '',
        ethnicity: propInitialData.ethnicity,
        dateOfBirth: propInitialData.dateOfBirth ? dayjs(propInitialData.dateOfBirth, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD') : undefined,
        gender: propInitialData.gender,
        religion: propInitialData.religion || '',
        maritalStatus: propInitialData.maritalStatus,
        militaryService: propInitialData.militaryService,
        personalTaxCode: propInitialData.personalTaxCode || '',
        startDate: propInitialData.startDate ? dayjs(propInitialData.startDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD') : undefined,
        officialContractDate: propInitialData.contractSigningDate ? dayjs(propInitialData.contractSigningDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD') : undefined,
        generalEducationLevel: propInitialData.educationLevel,
        yearsOfExperience: propInitialData.yearsOfExperience ? parseFloat(propInitialData.yearsOfExperience) : undefined,
        specialization: propInitialData.major || '',
        directManager: propInitialData.directManager || '',
        laborType: propInitialData.laborType,
        bankAccountNumber: propInitialData.bankAccountNumber || '',
        bankAccountName: propInitialData.bankAccountName || '',
        bankName: propInitialData.bankName || '',
        bankBranch: propInitialData.bankBranch || '',
      };
      setOriginalData(original);
      
      form.setFieldsValue({
        fullName: propInitialData.fullName,
        description: propInitialData.description,
        placeOfBirth: propInitialData.placeOfBirth || '',
        hometown: propInitialData.hometown || '',
        nationality: propInitialData.nationality || '',
        ethnicity: convertDbValueToFormValue('ethnicity', propInitialData.ethnicity),
        dateOfBirth: parseDate(propInitialData.dateOfBirth),
        gender: convertDbValueToFormValue('gender', propInitialData.gender),
        religion: propInitialData.religion,
        maritalStatus: convertDbValueToFormValue('maritalStatus', propInitialData.maritalStatus),
        militaryService: convertDbValueToFormValue('militaryService', propInitialData.militaryService),
        personalTaxCode: propInitialData.personalTaxCode,
        startDate: undefined,
        contractSigningDate: parseDate(propInitialData.contractSigningDate),
        educationLevel: convertDbValueToFormValue('educationLevel', propInitialData.educationLevel),
        yearsOfExperience: propInitialData.yearsOfExperience,
        major: propInitialData.major,
        highestEducationLevel: convertDbValueToFormValue('highestEducationLevel', propInitialData.highestEducationLevel),
        directManager: propInitialData.directManager,
        laborType: convertDbValueToFormValue('laborType', propInitialData.laborType),
        bankAccountNumber: propInitialData.bankAccountNumber || '',
        bankAccountName: propInitialData.bankAccountName || '',
        bankName: propInitialData.bankName || '',
        bankBranch: propInitialData.bankBranch || '',
      });
    }
  }, [propInitialData, form, parseDate, convertDbValueToFormValue]);

  // Handle submit - so sánh với giá trị ban đầu và chỉ gửi những trường có thay đổi
  const handleSubmit = useCallback(async () => {
    if (initialDataStatus !== 'ready' || !originalData) {
      message.error('Chưa tải đủ dữ liệu hồ sơ. Vui lòng tải lại trang trước khi cập nhật.');
      return 'failed' as const;
    }
    try {
      await form.validateFields();
      // Các mục của hồ sơ nằm ở nhiều tab; lấy cả giá trị của tab đã đóng.
      const values = form.getFieldsValue(true);
      setLoading(true);
      
      // Xác định user ID cần update
      let userId: number | undefined;
      if (personnelData?.id) {
        userId = parseInt(personnelData.id, 10);
      } else if (user?.id) {
        userId = parseInt(user.id.toString(), 10);
      }

      if (!userId || isNaN(userId)) {
        message.error('Không tìm thấy thông tin người dùng');
        return 'failed' as const;
      }

      // Convert dayjs objects to strings (YYYY-MM-DD format cho backend)
      // Handle array values from mode="tags" selects - convert to string (first element)
      const normalizedFamilyDependents = (values.familyDependents || [])
        .map((member: any) => ({
          relationship: member?.relationship || '',
          fullName: member?.fullName || '',
          birthYear:
            member?.birthYear !== undefined && member?.birthYear !== null
              ? String(member.birthYear)
              : '',
          occupation: member?.occupation || '',
          workplace: member?.workplace || '',
          phone: member?.phone || '',
          address: member?.address || '',
        }))
        .filter((member: any) =>
          member.relationship ||
          member.fullName ||
          member.birthYear ||
          member.occupation ||
          member.workplace ||
          member.phone ||
          member.address,
        );

      const normalizedEducationHistories = (values.educationHistories || [])
        .map((item: any) => ({
          fromDate: item?.fromDate ? dayjs(item.fromDate).format('YYYY-MM-DD') : undefined,
          toDate: item?.toDate ? dayjs(item.toDate).format('YYYY-MM-DD') : undefined,
          trainingType: item?.trainingType || '',
          major: item?.major || '',
          educationLevel: item?.educationLevel || '',
          school: item?.school || '',
        }))
        .filter(
          (item: any) =>
            item.fromDate ||
            item.toDate ||
            item.trainingType ||
            item.major ||
            item.educationLevel ||
            item.school,
        );

      let invalidWorkExperienceSalary = false;
      const normalizedWorkExperiences = (values.workExperiences || [])
        .map((item: any) => {
          const parsedSalary = item?.salary === undefined || item?.salary === null || item?.salary === ''
            ? undefined
            : parseCurrencyInput(item.salary);
          if (parsedSalary === null) invalidWorkExperienceSalary = true;
          return {
            fromDate: item?.fromDate ? dayjs(item.fromDate).format('YYYY-MM-DD') : undefined,
            toDate: item?.toDate ? dayjs(item.toDate).format('YYYY-MM-DD') : undefined,
            company: item?.company || '',
            department: item?.department || '',
            title: item?.title || '',
            position: item?.position || '',
            reference: item?.reference || '',
            salary: parsedSalary === null ? undefined : parsedSalary,
          };
        })
        .filter((item: any) =>
          item.fromDate || item.toDate || item.company || item.department || item.title ||
          item.position || item.reference || item.salary !== undefined,
        );
      if (invalidWorkExperienceSalary) {
        message.error('Mức lương trong kinh nghiệm làm việc phải là số hợp lệ');
        return 'failed' as const;
      }

      const normalizedCertificates = (values.certificates || [])
        .map((item: any) => ({
          id: item?.id,
          certificateNumber: item?.certificateNumber || '',
          issuingUnit: item?.issuingBody || '',
          certificateType: item?.certificateType || '',
          fromDate: item?.fromDate ? dayjs(item.fromDate).format('YYYY-MM-DD') : undefined,
          toDate: item?.toDate ? dayjs(item.toDate).format('YYYY-MM-DD') : undefined,
          notes: item?.notes || '',
        }))
        .filter(
          (item: any) =>
            item.certificateNumber ||
            item.issuingUnit ||
            item.certificateType ||
            item.fromDate ||
            item.toDate ||
            item.notes,
        );

      const normalizedSkills = (Array.isArray(values.skills) ? values.skills : [])
        .map((item: any) => String(item).trim())
        .filter(Boolean);

      const salary = values.currentSalary !== undefined && values.currentSalary !== null && values.currentSalary !== ''
        ? parseCurrencyInput(values.currentSalary)
        : undefined;
      if (salary === null) {
        message.error('Mức lương phải là số hợp lệ');
        return 'failed' as const;
      }

      const yearsOfExperience = values.yearsOfExperience
        ? Number(values.yearsOfExperience)
        : undefined;
      if (yearsOfExperience !== undefined && !Number.isFinite(yearsOfExperience)) {
        message.error('Số năm kinh nghiệm phải là số hợp lệ');
        return 'failed' as const;
      }

      const newData: any = {
        fullName: values.fullName,
        selfDescription: values.description,
        placeOfBirth: values.placeOfBirth,
        hometown: values.hometown,
        nationality: values.nationality,
        ethnicity: Array.isArray(values.ethnicity) ? values.ethnicity[0] : values.ethnicity,
        dateOfBirth: values.dateOfBirth ? dayjs(values.dateOfBirth).format('YYYY-MM-DD') : undefined,
        gender: values.gender,
        religion: Array.isArray(values.religion) ? values.religion[0] : values.religion,
        maritalStatus: values.maritalStatus,
        militaryService: values.militaryService,
        personalTaxCode: values.personalTaxCode,
        // Thông tin liên hệ
        phone: values.phone,
        personalEmail: values.personalEmail,
        permanentAddress: values.permanentAddress,
        permanentWard: values.permanentWard,
        currentAddress: values.currentAddress,
        currentWard: values.currentWard,
        // ID documents
        idNumber: values.idNumber,
        idIssuePlace: values.idIssuePlace,
        idIssueDate: values.idIssueDate ? dayjs(values.idIssueDate).format('YYYY-MM-DD') : undefined,
        idExpiryDate: values.idExpiryDate ? dayjs(values.idExpiryDate).format('YYYY-MM-DD') : undefined,
        passportType: values.passportType,
        passportNumber: values.passportNumber,
        passportIssuePlace: values.passportIssuePlace,
        passportIssueDate: values.passportIssueDate ? dayjs(values.passportIssueDate).format('YYYY-MM-DD') : undefined,
        passportExpiryDate: values.passportExpiryDate ? dayjs(values.passportExpiryDate).format('YYYY-MM-DD') : undefined,
        officialContractDate: values.contractSigningDate ? dayjs(values.contractSigningDate).format('YYYY-MM-DD') : undefined,
        generalEducationLevel: values.educationLevel,
        yearsOfExperience,
        specialization: values.major,
        directManager: values.directManager,
        laborType: values.laborType,
        company: values.currentCompany,
        departmentId: values.currentDepartment === undefined
          ? (form.isFieldTouched('currentDepartment') ? null : originalData?.departmentId ?? null)
          : values.currentDepartment,
        position: values.currentTitle,
        jobLocation: values.currentPosition,
        referencePerson: values.currentReference,
        salary,
        startDate: values.currentStartDate ? dayjs(values.currentStartDate).format('YYYY-MM-DD') : undefined,
        bankAccountNumber: values.bankAccountNumber,
        bankAccountName: values.bankAccountName,
        bankName: values.bankName,
        bankBranch: values.bankBranch,
        familyDependents: normalizedFamilyDependents,
        educationHistories: normalizedEducationHistories,
        workExperiences: normalizedWorkExperiences,
        certificates: normalizedCertificates,
        skills: normalizedSkills,
      };

      // Nếu không có trường nào thay đổi, báo lỗi
      const payload = buildUserUpdatePayload(newData, originalData, editedCollections.current);
      if (Object.keys(payload).length === 0) {
        return 'no-changes' as const;
      }

      // Call API to update user
      let response;
      let skippedSkillsForCompatibility = false;

      try {
        response = await usersService.updateUser(userId, payload);
      } catch (error: any) {
        const rawMessage = String(error?.message || '');
        const shouldRetryWithoutSkills =
          rawMessage.includes('property skills should not exist') &&
          Object.prototype.hasOwnProperty.call(payload, 'skills');

        if (!shouldRetryWithoutSkills) {
          throw error;
        }

        const { skills, ...payloadWithoutSkills } = payload;
        response = await usersService.updateUser(userId, payloadWithoutSkills);
        skippedSkillsForCompatibility = true;
      }
      
      if (response.success) {
        if (skippedSkillsForCompatibility) {
          message.warning(
            'Đã lưu thông tin thành công, nhưng backend hiện tại chưa hỗ trợ lưu kỹ năng. Vui lòng chạy lại backend mới nhất để bật tính năng này.',
          );
        } else {
          message.success(response.requestId
            ? `Đã gửi yêu cầu #${response.requestId} với ${Object.keys(payload).length} mục cập nhật, chờ HR/Admin phê duyệt`
            : response.message || 'Cập nhật thông tin thành công');
        }
        
        // Nếu có custom onSubmit handler, gọi nó
        if (onSubmit) {
          const formData: PersonalInfoFormData = {
            ...values,
            dateOfBirth: values.dateOfBirth ? formatDateDisplay(values.dateOfBirth) : undefined,
            startDate: values.startDate ? formatDateDisplay(values.startDate) : undefined,
            contractSigningDate: values.contractSigningDate ? formatDateDisplay(values.contractSigningDate) : undefined,
            profilePicture: profileImage || undefined,
          };
          await onSubmit(formData);
        }
        return 'submitted' as const;
      } else {
        message.error(response.message || 'Cập nhật thông tin thất bại');
        return 'failed' as const;
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      handleApiError(error, 'Không thể cập nhật thông tin. Vui lòng thử lại.');
      return 'failed' as const;
    } finally {
      setLoading(false);
    }
  }, [form, personnelData, user, originalData, initialDataStatus, profileImage, onSubmit]);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleImageUpload = useCallback(async (file: File): Promise<boolean> => {
    // Kiểm tra quyền chỉnh sửa avatar
    if (!canEditAvatar) {
      message.warning('Bạn không có quyền cập nhật ảnh đại diện cho người này.');
      return false;
    }

    // Xác định user ID để upload (có thể là chính mình hoặc staff đang được chỉnh sửa)
    const targetUserId = personnelData?.id 
      ? parseInt(personnelData.id, 10)
      : (user?.id ? parseInt(user.id.toString(), 10) : null);

    if (!targetUserId || isNaN(targetUserId)) {
      message.error('Không tìm thấy thông tin người dùng.');
      return false;
    }

    // Hiển thị preview ngay
    const reader = new FileReader();
    reader.onloadend = () => {
      const previewDataUri = reader.result as string;
      setProfileImage(previewDataUri);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    isUploadingRef.current = true; // Đánh dấu đang upload
    
    try {
      const response = await usersService.uploadAvatar(targetUserId, file);
      
      if (response.success && response.data) {
        // Lấy profile mới từ server để có avatar URL đầy đủ
        try {
          // Nếu đang chỉnh sửa hồ sơ của chính mình, dùng getProfile()
          // Nếu đang chỉnh sửa hồ sơ của người khác, dùng getUserById()
          let profile;
          if (isViewingOwnProfile) {
            profile = await usersService.getProfile();
          } else {
            profile = await usersService.getUserById(targetUserId);
          }
          
          let finalAvatarUrl: string | null = null;
          
          if (profile?.avatar) {
            finalAvatarUrl = buildFullUrl(profile.avatar);
          } else if (response.data.url) {
            finalAvatarUrl = buildFullUrl(response.data.url);
          } else if (response.data.relativeUrl) {
            finalAvatarUrl = buildFullUrl(response.data.relativeUrl);
          }

          if (finalAvatarUrl) {
            // Convert URL sang data URI để hiển thị ngay (không cần fetch lại)
            const dataUri = await fetchImageAsDataUri(finalAvatarUrl);
            const avatarToDisplay = dataUri || finalAvatarUrl;
            
            // Chỉ cập nhật avatarUri trong context nếu đang chỉnh sửa hồ sơ của chính mình
            if (isViewingOwnProfile) {
              await setAvatarUri(avatarToDisplay);
            }
            
            // Set avatar để hiển thị (dùng data URI để hiển thị ngay)
            setProfileImage(avatarToDisplay);
            message.success('Đã cập nhật ảnh đại diện thành công.');
            
            // Dispatch custom event để các component khác biết cần refresh avatar
            const userId = isViewingOwnProfile ? user?.id : targetUserId;
            if (userId) {
              window.dispatchEvent(new CustomEvent('avatar-updated', { 
                detail: { userId: userId.toString() } 
              }));
            }
          } else {
            message.warning('Đã upload nhưng không thể lấy URL avatar từ server.');
          }
        } catch (profileError) {
          if (response.data.url) {
            const fallbackUrl = buildFullUrl(response.data.url);
            if (fallbackUrl) {
              // Convert URL sang data URI
              const dataUri = await fetchImageAsDataUri(fallbackUrl);
              const avatarToDisplay = dataUri || fallbackUrl;
              
              // Chỉ cập nhật avatarUri trong context nếu đang chỉnh sửa hồ sơ của chính mình
              if (isViewingOwnProfile) {
                await setAvatarUri(avatarToDisplay);
              }
              setProfileImage(avatarToDisplay);
              
              // Dispatch custom event để các component khác biết cần refresh avatar
              const userId = isViewingOwnProfile ? user?.id : targetUserId;
              if (userId) {
                window.dispatchEvent(new CustomEvent('avatar-updated', { 
                  detail: { userId: userId.toString() } 
                }));
              }
            }
          }
          message.success('Đã cập nhật ảnh đại diện thành công.');
        }
      } else {
        throw new Error(response.message || 'Upload thất bại');
      }
    } catch (error: any) {
      message.error(
        getUserFriendlyErrorMessage(
          error,
          'Không thể cập nhật ảnh đại diện. Vui lòng thử lại.',
        ),
      );
      // Giữ lại preview local nếu upload thất bại
    } finally {
      setUploading(false);
      // Đợi một chút rồi mới cho phép useEffect chạy lại (để avatar đã được set xong)
      setTimeout(() => {
        isUploadingRef.current = false;
      }, 100);
    }

    return false; // Prevent default upload behavior
  }, [canEditAvatar, isViewingOwnProfile, user, personnelData, setAvatarUri]);

  const handleMenuClick = useCallback((e: { key: string }) => {
    setSelectedMenuKey(e.key);
  }, []);

  return {
    // form
    form,
    loading,
    initialDataStatus,
    uploading,
    profileImage,
    setProfileImage,
    
    // UI state
    sidebarCollapsed,
    setSidebarCollapsed,
    selectedMenuKey,
    setSelectedMenuKey,
    isHovered,
    setIsHovered,
    
    // computed
    isViewingOwnProfile,
    canEditAvatar,
    
    // actions
    handleSubmit,
    handleValuesChange,
    handleCancel,
    handleImageUpload,
    handleMenuClick,
    parseDate,
    convertDbValueToFormValue,
  };
};
